import React, { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { RefreshCcw, Database, Users, AlertTriangle, CheckCircle, Info } from 'lucide-react'
import { Button } from './ui'
import { resetSharedDocument, getSharedDocumentStatus, migrateToFreshDocument } from '../utils/sharedDocumentReset.js'

export default function SharedDocumentManager({ wallet, onComplete }) {
  const [status, setStatus] = useState('idle') // idle, checking, resetting, complete, error
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('')
  const [documentStats, setDocumentStats] = useState(null)
  const [resetResults, setResetResults] = useState(null)

  // Check current shared document status
  const checkDocumentStatus = useCallback(async () => {
    setStatus('checking')
    setMessage('Checking shared document status...')
    
    try {
      const stats = await getSharedDocumentStatus()
      setDocumentStats(stats)
      setMessage('Document status loaded')
      setStatus('idle')
    } catch (error) {
      console.error('Failed to check document status:', error)
      setMessage(`Error checking status: ${error.message}`)
      setStatus('error')
    }
  }, [])

  // Reset shared document
  const resetDocument = useCallback(async () => {
    if (!wallet?.address) {
      setMessage('Wallet address required for reset')
      setStatus('error')
      return
    }

    setStatus('resetting')
    setProgress(0)
    setMessage('Starting shared document reset...')

    try {
      const result = await resetSharedDocument({
        preserveUserData: true,
        forceReset: true,
        onProgress: ({ step, message, progress }) => {
          setProgress(progress)
          setMessage(message)
        }
      })

      setResetResults(result)
      setStatus('complete')
      setMessage('Shared document reset successfully!')
      
      // Refresh document status
      await checkDocumentStatus()
      
      if (onComplete) {
        onComplete(result)
      }

    } catch (error) {
      console.error('Reset failed:', error)
      setMessage(`Reset failed: ${error.message}`)
      setStatus('error')
    }
  }, [wallet?.address, onComplete, checkDocumentStatus])

  // Migrate to fresh document
  const migrateDocument = useCallback(async () => {
    if (!wallet?.address) {
      setMessage('Wallet address required for migration')
      setStatus('error')
      return
    }

    setStatus('resetting')
    setProgress(0)
    setMessage('Starting migration to fresh document...')

    try {
      const result = await migrateToFreshDocument(wallet.address, {
        onProgress: ({ step, message, progress }) => {
          setProgress(progress)
          setMessage(message)
        }
      })

      setResetResults(result)
      setStatus('complete')
      setMessage('Migration completed successfully!')
      
      // Refresh document status
      await checkDocumentStatus()
      
      if (onComplete) {
        onComplete(result)
      }

    } catch (error) {
      console.error('Migration failed:', error)
      setMessage(`Migration failed: ${error.message}`)
      setStatus('error')
    }
  }, [wallet?.address, onComplete, checkDocumentStatus])

  // Auto-check status on mount
  React.useEffect(() => {
    checkDocumentStatus()
  }, [checkDocumentStatus])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto p-6 space-y-6"
    >
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
          <Database className="w-6 h-6" />
          Shared Document Manager
        </h2>
        <p className="text-sm opacity-70">
          Manage the shared public archive document for cross-user IPFS memory sharing
        </p>
      </div>

      {/* Current Status */}
      {documentStats && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3"
        >
          <h3 className="font-semibold flex items-center gap-2">
            <Info className="w-4 h-4" />
            Current Document Status
          </h3>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="opacity-70">Total Memories:</span>
              <div className="font-mono">{documentStats.totalMemories}</div>
            </div>
            <div>
              <span className="opacity-70">With IPFS:</span>
              <div className="font-mono text-green-600">{documentStats.memoriesWithIPFS}</div>
            </div>
            <div>
              <span className="opacity-70">Without IPFS:</span>
              <div className="font-mono text-red-600">{documentStats.memoriesWithoutIPFS}</div>
            </div>
            <div>
              <span className="opacity-70">Unique Users:</span>
              <div className="font-mono">{documentStats.uniqueUsers}</div>
            </div>
          </div>

          {documentStats.version && (
            <div className="text-xs opacity-60">
              Version: {documentStats.version} • Created: {documentStats.created ? new Date(documentStats.created).toLocaleDateString() : 'Unknown'}
            </div>
          )}
        </motion.div>
      )}

      {/* Progress Display */}
      {(status === 'checking' || status === 'resetting') && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-3"
        >
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.max(progress, 10)}%` }}
            />
          </div>
          <p className="text-sm text-center opacity-70">{message}</p>
        </motion.div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          onClick={checkDocumentStatus}
          disabled={status === 'checking' || status === 'resetting'}
          variant="outline"
          className="flex items-center justify-center gap-2"
        >
          <RefreshCcw className={`w-4 h-4 ${status === 'checking' ? 'animate-spin' : ''}`} />
          Check Status
        </Button>

        <Button
          onClick={resetDocument}
          disabled={status === 'checking' || status === 'resetting'}
          variant="ghost"
          className="flex items-center justify-center gap-2 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
        >
          <AlertTriangle className="w-4 h-4" />
          Reset Document
        </Button>

        <Button
          onClick={migrateDocument}
          disabled={status === 'checking' || status === 'resetting'}
          className="flex items-center justify-center gap-2"
        >
          <Users className="w-4 h-4" />
          Fresh Start
        </Button>
      </div>

      {/* Action Descriptions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs opacity-60">
        <div className="text-center">
          Refresh the current document statistics and check connectivity
        </div>
        <div className="text-center">
          Clear and reset the shared document while preserving user data
        </div>
        <div className="text-center">
          Create a completely fresh shared document for all users
        </div>
      </div>

      {/* Success/Error Messages */}
      {status === 'complete' && resetResults && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
        >
          <div className="flex items-center gap-2 text-green-800 dark:text-green-200 font-semibold mb-2">
            <CheckCircle className="w-4 h-4" />
            Success!
          </div>
          <p className="text-sm text-green-700 dark:text-green-300 mb-3">
            {resetResults.message}
          </p>
          
          {resetResults.verification && (
            <div className="text-xs space-y-1 opacity-80">
              <div>Document ID: {resetResults.verification.documentId}</div>
              <div>Client ID: {resetResults.verification.clientId}</div>
              <div>Version: {resetResults.verification.version}</div>
            </div>
          )}
        </motion.div>
      )}

      {status === 'error' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4"
        >
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200 font-semibold mb-2">
            <AlertTriangle className="w-4 h-4" />
            Error
          </div>
          <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="text-xs opacity-50 text-center space-y-1 pt-4 border-t">
        <p>💡 <strong>Fresh Start</strong> creates a completely new shared document that all users will sync to</p>
        <p>🔄 <strong>Reset Document</strong> clears the current document but may preserve some structure</p>
        <p>📊 <strong>Check Status</strong> shows current document statistics and connectivity</p>
      </div>
    </motion.div>
  )
}
